import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import modalStyles from '@/features/application/styles/modals.module.css'
import { policyFormSchema, type PolicyFormValues } from '../types/schemas'
import type { PolicyEditModalProps } from '../types'
import { POLICY_GROUPS } from '../types'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'

/** Map API value (key or display label) to a POLICY_GROUPS key for the form. */
function toPolicyGroupKey(value: string | undefined): string {
  if (value == null || value === '') return ''
  if (value in POLICY_GROUPS) return value
  const entry = (Object.keys(POLICY_GROUPS) as Array<keyof typeof POLICY_GROUPS>).find(
    (k) => POLICY_GROUPS[k] === value,
  )
  return entry ?? value
}

export function PolicyEditModal({
  isOpen,
  policy,
  submitting,
  onClose,
  onSubmit,
}: PolicyEditModalProps) {
  const isEdit = Boolean(policy?.policy_key)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<PolicyFormValues>({
    resolver: zodResolver(policyFormSchema),
    mode: 'onChange',
    defaultValues: { policy_name: '', policy_group: '', policy_description: '' },
  })

  // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form watch() is intentionally unmemoizable
  const groupKey = watch('policy_group') as keyof typeof POLICY_GROUPS | string
  const groupLabel =
    groupKey && groupKey in POLICY_GROUPS
      ? POLICY_GROUPS[groupKey as keyof typeof POLICY_GROUPS]
      : (policy?.policy_group as string) ?? '—'

  /** Avoid resetting the form on every `policy` object reference change (breaks isDirty and feels “stuck”). */
  const policyLoadKey = useMemo(
    () =>
      policy
        ? [
            policy.policy_key ?? '',
            policy.policy_name ?? '',
            String(policy.policy_group ?? ''),
            policy.policy_description ?? '',
          ].join('\0')
        : '',
    [policy],
  )

  useEffect(() => {
    if (!isOpen) return
    reset({
      policy_name: policy?.policy_name ?? '',
      policy_group: toPolicyGroupKey(policy?.policy_group as string | undefined),
      policy_description: policy?.policy_description ?? '',
    })
  }, [isOpen, policyLoadKey, reset])

  const internalSubmit = (values: PolicyFormValues) => {
    onSubmit(
      {
        policy_name: values.policy_name.trim(),
        policy_group: values.policy_group,
        policy_description: values.policy_description.trim(),
      },
      policy?.policy_key,
    )
  }

  return (
    <OnScreenModal
      isOpen={isOpen}
      title={policy?.policy_key ? 'Edit Policy' : 'Add Policy'}
      onClose={onClose}
      maxWidth="max-w-lg"
    >
      <form onSubmit={handleSubmit(internalSubmit)} className="space-y-4 p-2">
        <div>
          <label htmlFor="policy-name" className="block text-sm font-medium text-slate-700 mb-1">
            Policy Name
          </label>
          <input
            id="policy-name"
            type="text"
            readOnly={isEdit}
            className={`w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1597ff] focus:border-[#1597ff] ${
              isEdit ? 'bg-slate-50 cursor-not-allowed' : ''
            }`}
            {...register('policy_name')}
          />
          {errors.policy_name && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.policy_name.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="policy-group" className="block text-sm font-medium text-slate-700 mb-1">
            Policy Group
          </label>
          {isEdit ? (
            <>
              {/*
                Do not use disabled on registered fields — RHF omits them from validation,
                so isValid stays false and Submit stays disabled. Hidden + read-only display instead.
              */}
              <input type="hidden" {...register('policy_group')} />
              <div
                id="policy-group"
                className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-slate-800"
              >
                {groupLabel}
              </div>
            </>
          ) : (
            <select
              id="policy-group"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1597ff] focus:border-[#1597ff]"
              {...register('policy_group')}
            >
              <option value="">Select group…</option>
              {(Object.keys(POLICY_GROUPS) as Array<keyof typeof POLICY_GROUPS>).map((key) => (
                <option key={key} value={key}>
                  {POLICY_GROUPS[key]}
                </option>
              ))}
            </select>
          )}
          {errors.policy_group && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.policy_group.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="policy-description"
            className="block text-sm font-medium text-slate-700 mb-1"
          >
            Policy Description
          </label>
          <textarea
            id="policy-description"
            rows={4}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1597ff] focus:border-[#1597ff]"
            {...register('policy_description')}
          />
          {errors.policy_description && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.policy_description.message}
            </p>
          )}
        </div>

        <div className="flex justify-center gap-3 pt-4 border-t border-slate-200 mt-6">
          <button
            type="submit"
            disabled={submitting}
            className={`${primaryEntityActionButtonClass} min-w-[120px]`}
          >
            {submitting ? 'Saving...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={`${secondaryEntityActionButtonClass} min-w-[120px]`}
          >
            Cancel
          </button>
        </div>
      </form>
    </OnScreenModal>
  )
}
